import { createElement, type ComponentType, type ReactNode } from "react";
import {
  ArrowDownIcon,
  CheckIcon,
  ChevronDownIcon,
  CopyIcon,
  CopyStrokeIcon,
  DiscardCardIcon,
  ErrorAlertIcon,
  HowToDepositHelpIcon,
  InfoCircleIcon,
  LimitInfoIcon,
  MailStrokeIcon,
  MapPinIcon,
  ShareStrokeIcon,
  StatusCircleIcon,
  StatusFailedIcon,
  StatusPendingIcon,
  StorefrontIcon,
  SupportStrokeIcon,
  NotificationBellIcon,
  WhatsAppStrokeIcon,
} from "../assets/icons";
import { RotateDeviceIcon } from "../components/RotateDeviceIcon";
import {
  GoogleMark,
  MicrosoftMark,
} from "../components/consumer-auth/SocialAuthButton";
import visa from "../assets/visa_color_icon.svg";
import mastercard from "../assets/master_card_icon.svg";
import amex from "../assets/amex_icon.svg";
import discover from "../assets/discover_icon.svg";
import kapital from "../assets/KapitalLogoR.svg";
import depositStep1 from "../assets/cash_deposit_how_to/step-deposit-tap.svg?raw";
import lolaDepositStep1 from "../assets/cash_deposit_how_to/step1.svg?raw";
import depositStep2 from "../assets/cash_deposit_how_to/step2.svg?raw";
import depositStep3 from "../assets/cash_deposit_how_to/step3.svg?raw";
import depositStep4 from "../assets/cash_deposit_how_to/step4.svg?raw";
import depositStep5 from "../assets/cash_deposit_how_to/step5.svg?raw";
import {
  AddCardIcon,
  BackArrow,
  BankIcon,
  BankV2Icon,
  CardIcon,
  CashIcon,
  CashSpecialIcon,
  CheckCircleIcon,
  Close,
  DebitCardIcon,
  ErrorCircleIcon,
  ExchangeIcon,
  ExchangeV2Icon,
  HomeAddressIcon,
  KapitalIcon,
  LolaLogo,
  MoreCircleIcon,
  RightIcon,
  RoundedCheckIcon,
  SuccessIcon,
  SupportIcon,
  SupportV2Icon,
  WalletIcon,
  WarningCircleIcon,
  WarningIcon,
} from "lola-framework-ui-test";

export const ICON_CATALOG_ROUTE = "/icons";

export type IconCatalogTab = "icons" | "components" | "brands";

export type IconPaint = {
  size: number;
  colors: [string, string];
  ink: string;
  glyph: string;
  onDark: boolean;
};

export type IconCatalogEntry = {
  id: string;
  name: string;
  tab: IconCatalogTab;
  /** `lola` icons render in the second group of the Icons tab. */
  source?: "lola";
  note?: string;
  render: (paint: IconPaint) => ReactNode;
};

type StrokeIcon = ComponentType<{
  size?: number;
  colors?: [string, string];
}>;

type InkIcon = ComponentType<{ size?: number; color?: string }>;

type SizedIcon = ComponentType<{ size?: number }>;

const stroke = (Icon: StrokeIcon) => (paint: IconPaint) =>
  createElement(Icon, { size: paint.size, colors: paint.colors });

const ink = (Icon: InkIcon) => (paint: IconPaint) =>
  createElement(Icon, { size: paint.size, color: paint.ink });

const currentColor = (Icon: SizedIcon) => (paint: IconPaint) =>
  createElement(
    "span",
    {
      style: {
        color: paint.ink,
        display: "inline-flex",
        lineHeight: 0,
      },
    },
    createElement(Icon, { size: paint.size }),
  );

const sized = (Icon: SizedIcon) => (paint: IconPaint) =>
  createElement(Icon, { size: paint.size });

const illustration = (svg: string) => (paint: IconPaint) =>
  createElement("span", {
    className: "icon-catalog__art",
    style: { width: paint.size, height: paint.size },
    dangerouslySetInnerHTML: { __html: svg },
  });

const brand = (src: string) => (paint: IconPaint) =>
  createElement("img", {
    src,
    alt: "",
    style: {
      width: paint.size * 1.6,
      height: "auto",
      maxHeight: paint.size,
      objectFit: "contain",
    },
  });

export const iconCatalog: IconCatalogEntry[] = [
  {
    id: "contact.whatsapp",
    name: "WhatsAppStrokeIcon",
    tab: "icons",
    render: stroke(WhatsAppStrokeIcon),
  },
  {
    id: "contact.mail",
    name: "MailStrokeIcon",
    tab: "icons",
    render: stroke(MailStrokeIcon),
  },
  {
    id: "action.copy",
    name: "CopyStrokeIcon",
    tab: "icons",
    render: stroke(CopyStrokeIcon),
  },
  {
    id: "action.copyFilled",
    name: "CopyIcon",
    tab: "icons",
    render: currentColor(CopyIcon),
  },
  {
    id: "action.check",
    name: "CheckIcon",
    tab: "icons",
    render: currentColor(CheckIcon),
  },
  {
    id: "action.share",
    name: "ShareStrokeIcon",
    tab: "icons",
    render: (paint) =>
      createElement(ShareStrokeIcon, {
        size: paint.size,
        colors: paint.onDark ? ["#1a1a1a", "#1a1a1a"] : paint.colors,
      }),
  },
  {
    id: "action.arrowDown",
    name: "ArrowDownIcon",
    tab: "icons",
    render: stroke(ArrowDownIcon),
  },
  {
    id: "status.neutral",
    name: "StatusCircleIcon",
    tab: "icons",
    render: ink(StatusCircleIcon),
  },
  {
    id: "status.pending",
    name: "StatusPendingIcon",
    tab: "icons",
    render: ink(StatusPendingIcon),
  },
  {
    id: "status.failed",
    name: "StatusFailedIcon",
    tab: "icons",
    render: ink(StatusFailedIcon),
  },
  {
    id: "info.limit",
    name: "LimitInfoIcon",
    tab: "icons",
    render: stroke(LimitInfoIcon),
  },
  {
    id: "info.circle",
    name: "InfoCircleIcon",
    tab: "icons",
    render: stroke(InfoCircleIcon),
  },
  {
    id: "alert.error",
    name: "ErrorAlertIcon",
    tab: "icons",
    render: stroke(ErrorAlertIcon),
  },
  {
    id: "support.stroke",
    name: "SupportStrokeIcon",
    tab: "icons",
    render: (paint) =>
      createElement(SupportStrokeIcon, {
        size: paint.size,
        colors: paint.colors,
        glyphColor: paint.glyph,
      }),
  },
  {
    id: "notifications.bell",
    name: "NotificationBellIcon",
    tab: "icons",
    render: ink(NotificationBellIcon),
  },
  {
    id: "help.deposit",
    name: "HowToDepositHelpIcon",
    tab: "icons",
    render: stroke(HowToDepositHelpIcon),
  },
  {
    id: "card.discard",
    name: "DiscardCardIcon",
    tab: "icons",
    note: "Canonical drawing from screenbook. flow-lola uses a different drawing.",
    render: stroke(DiscardCardIcon),
  },
  {
    id: "place.store",
    name: "StorefrontIcon",
    tab: "icons",
    render: stroke(StorefrontIcon),
  },
  {
    id: "device.rotate",
    name: "RotateDeviceIcon",
    tab: "icons",
    render: stroke(RotateDeviceIcon),
  },
  {
    id: "action.chevronDown",
    name: "ChevronDownIcon",
    tab: "icons",
    render: currentColor(ChevronDownIcon),
  },
  {
    id: "place.mapPin",
    name: "MapPinIcon",
    tab: "icons",
    render: ink(MapPinIcon),
  },
  {
    id: "illustration.deposit.step1",
    name: "step-deposit-tap.svg",
    tab: "components",
    note: "Canonical deposit set. Lola step 1 is illustration.lola.deposit.step1.",
    render: illustration(depositStep1),
  },
  {
    id: "illustration.deposit.step2",
    name: "step2.svg",
    tab: "components",
    render: illustration(depositStep2),
  },
  {
    id: "illustration.deposit.step3",
    name: "step3.svg",
    tab: "components",
    render: illustration(depositStep3),
  },
  {
    id: "illustration.deposit.step4",
    name: "step4.svg",
    tab: "components",
    render: illustration(depositStep4),
  },
  {
    id: "illustration.deposit.step5",
    name: "step5.svg",
    tab: "components",
    render: illustration(depositStep5),
  },
  {
    id: "illustration.lola.deposit.step1",
    name: "step1.svg",
    tab: "components",
    note: "Lola how-to carousel, step 1. Steps 2–5 on that screen use the canonical drawings.",
    render: illustration(lolaDepositStep1),
  },
  {
    id: "brand.visa",
    name: "visa_color_icon.svg",
    tab: "brands",
    render: brand(visa),
  },
  {
    id: "brand.mastercard",
    name: "master_card_icon.svg",
    tab: "brands",
    render: brand(mastercard),
  },
  {
    id: "brand.amex",
    name: "amex_icon.svg",
    tab: "brands",
    render: brand(amex),
  },
  {
    id: "brand.discover",
    name: "discover_icon.svg",
    tab: "brands",
    render: brand(discover),
  },
  {
    id: "brand.kapital",
    name: "KapitalLogoR.svg",
    tab: "brands",
    render: brand(kapital),
  },
  {
    id: "brand.google",
    name: "GoogleMark",
    tab: "brands",
    render: sized(GoogleMark),
  },
  {
    id: "brand.microsoft",
    name: "MicrosoftMark",
    tab: "brands",
    render: sized(MicrosoftMark),
  },
  {
    id: "status.successFilled",
    name: "CheckCircleIcon",
    tab: "icons",
    source: "lola",
    note: "Semantic green. flow-m2m still ships a local copy.",
    render: sized(CheckCircleIcon),
  },
  {
    id: "alert.errorCircle",
    name: "ErrorCircleIcon",
    tab: "icons",
    source: "lola",
    note: "Semantic red. flow-m2m still ships a local copy.",
    render: sized(ErrorCircleIcon),
  },
  {
    id: "alert.warning",
    name: "WarningCircleIcon",
    tab: "icons",
    source: "lola",
    note: "Semantic amber. flow-m2m still ships a local copy.",
    render: sized(WarningCircleIcon),
  },
  {
    id: "action.more",
    name: "MoreCircleIcon",
    tab: "icons",
    source: "lola",
    note: "Semantic gray. flow-m2m still ships a local copy.",
    render: sized(MoreCircleIcon),
  },
  {
    id: "card.add",
    name: "AddCardIcon",
    tab: "icons",
    source: "lola",
    note: "Replaces the local AddCardV2Icon.",
    render: stroke(AddCardIcon),
  },
  {
    id: "lola.backArrow",
    name: "BackArrow",
    tab: "icons",
    source: "lola",
    render: stroke(BackArrow),
  },
  {
    id: "lola.bank",
    name: "BankIcon",
    tab: "icons",
    source: "lola",
    render: stroke(BankIcon),
  },
  {
    id: "lola.bankV2",
    name: "BankV2Icon",
    tab: "icons",
    source: "lola",
    render: stroke(BankV2Icon),
  },
  {
    id: "lola.card",
    name: "CardIcon",
    tab: "icons",
    source: "lola",
    render: stroke(CardIcon),
  },
  {
    id: "lola.cash",
    name: "CashIcon",
    tab: "icons",
    source: "lola",
    render: stroke(CashIcon),
  },
  {
    id: "money.cash",
    name: "CashSpecialIcon",
    tab: "icons",
    source: "lola",
    note: "Replaces the local cash-note mark, including the Later interstitial.",
    render: stroke(CashSpecialIcon),
  },
  {
    id: "lola.close",
    name: "Close",
    tab: "icons",
    source: "lola",
    render: stroke(Close),
  },
  {
    id: "lola.debitCard",
    name: "DebitCardIcon",
    tab: "icons",
    source: "lola",
    render: stroke(DebitCardIcon),
  },
  {
    id: "lola.exchange",
    name: "ExchangeIcon",
    tab: "icons",
    source: "lola",
    render: stroke(ExchangeIcon),
  },
  {
    id: "lola.exchangeV2",
    name: "ExchangeV2Icon",
    tab: "icons",
    source: "lola",
    render: stroke(ExchangeV2Icon),
  },
  {
    id: "lola.homeAddress",
    name: "HomeAddressIcon",
    tab: "icons",
    source: "lola",
    render: stroke(HomeAddressIcon),
  },
  {
    id: "lola.kapital",
    name: "KapitalIcon",
    tab: "icons",
    source: "lola",
    render: stroke(KapitalIcon),
  },
  {
    id: "lola.logo",
    name: "LolaLogo",
    tab: "icons",
    source: "lola",
    render: stroke(LolaLogo),
  },
  {
    id: "lola.right",
    name: "RightIcon",
    tab: "icons",
    source: "lola",
    render: stroke(RightIcon),
  },
  {
    id: "status.success",
    name: "RoundedCheckIcon",
    tab: "icons",
    source: "lola",
    note: "Replaces the local CheckCircleStrokeIcon.",
    render: stroke(RoundedCheckIcon),
  },
  {
    id: "lola.success",
    name: "SuccessIcon",
    tab: "icons",
    source: "lola",
    render: stroke(SuccessIcon),
  },
  {
    id: "lola.support",
    name: "SupportIcon",
    tab: "icons",
    source: "lola",
    render: stroke(SupportIcon),
  },
  {
    id: "lola.supportV2",
    name: "SupportV2Icon",
    tab: "icons",
    source: "lola",
    render: stroke(SupportV2Icon),
  },
  {
    id: "lola.wallet",
    name: "WalletIcon",
    tab: "icons",
    source: "lola",
    render: stroke(WalletIcon),
  },
  {
    id: "lola.warning",
    name: "WarningIcon",
    tab: "icons",
    source: "lola",
    render: stroke(WarningIcon),
  },
];
